import { useState } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Download, FileText, Loader2, Trash2, Upload } from "lucide-react";

function formatarMoeda(valor: unknown) {
  return Number(valor ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatarData(valor: unknown) {
  if (!valor) return "-";
  return new Date(String(valor)).toLocaleString("pt-BR");
}

function textoStatus(status: string) {
  if (status === "rascunho") return "Rascunho";
  if (status === "gerado") return "Gerado";
  return "Cancelado";
}

function baixarBase64(nomeArquivo: string, base64: string) {
  const binario = atob(base64);
  const bytes = Uint8Array.from(binario, (caractere) => caractere.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "text/plain;charset=iso-8859-1" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function BankPayments() {
  const utils = trpc.useUtils();
  const [numeroRemessaSelecionado, setNumeroRemessaSelecionado] = useState<number | null>(null);
  const [arquivoRetorno, setArquivoRetorno] = useState<File | null>(null);
  const remessas = trpc.remessasPagamento.list.useQuery();
  const detalhes = trpc.remessasPagamento.detalhes.useQuery(
    { numeroRemessa: numeroRemessaSelecionado ?? 0 },
    { enabled: numeroRemessaSelecionado !== null }
  );
  const baixarArquivoCnab = trpc.remessasPagamento.baixarArquivo.useQuery(
    { numeroRemessa: numeroRemessaSelecionado ?? 0, tipo: "cnab" },
    { enabled: false }
  );
  const baixarArquivoRetorno = trpc.remessasPagamento.baixarArquivo.useQuery(
    { numeroRemessa: numeroRemessaSelecionado ?? 0, tipo: "retorno" },
    { enabled: false }
  );
  const gerarArquivo = trpc.remessasPagamento.gerarArquivo.useMutation({
    onSuccess: () => {
      toast.success("Arquivo CNAB 240 gerado.");
      utils.remessasPagamento.invalidate();
    },
    onError: (erro) => toast.error(erro.message),
  });
  const removerPagamento = trpc.remessasPagamento.removerPagamento.useMutation({
    onSuccess: () => {
      toast.success("Pagamento removido da remessa e devolvido para pendente.");
      utils.remessasPagamento.invalidate();
      utils.contasPagar.invalidate();
    },
    onError: (erro) => toast.error(erro.message),
  });
  const cancelarRemessa = trpc.remessasPagamento.cancelar.useMutation({
    onSuccess: () => {
      toast.success("Remessa cancelada e pagamentos devolvidos para pendente.");
      utils.remessasPagamento.invalidate();
      utils.contasPagar.invalidate();
    },
    onError: (erro) => toast.error(erro.message),
  });
  const enviarRetorno = trpc.remessasPagamento.enviarEProcessarRetorno.useMutation({
    onSuccess: (resultado) => {
      toast.success(`Retorno processado: ${resultado.efetivados} efetivado(s) e ${resultado.cancelados} devolvido(s) para pendente.`);
      setArquivoRetorno(null);
      utils.remessasPagamento.invalidate();
      utils.contasPagar.invalidate();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const itens = (detalhes.data ?? []) as any[];
  const remessaSelecionada = (remessas.data ?? []).find((remessa: any) => Number(remessa.numeroRemessa) === numeroRemessaSelecionado) as any;
  const statusSelecionado = remessaSelecionada?.status;
  const retornoProcessado = itens.some((item) => item.retornoProcessadoEm);

  const baixar = async (tipo: "cnab" | "retorno") => {
    if (!numeroRemessaSelecionado) return;
    const resultado = tipo === "cnab" ? await baixarArquivoCnab.refetch() : await baixarArquivoRetorno.refetch();
    const arquivo: any = resultado.data;
    if (!arquivo) return;
    baixarBase64(arquivo.nomeArquivo, arquivo.base64);
  };

  const processarArquivoRetorno = async () => {
    if (!numeroRemessaSelecionado || !arquivoRetorno) return;
    const base64 = await new Promise<string>((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onerror = () => reject(new Error("Não foi possível ler o arquivo de retorno."));
      leitor.onload = () => resolve(String(leitor.result));
      leitor.readAsDataURL(arquivoRetorno);
    });
    enviarRetorno.mutate({ numeroRemessa: numeroRemessaSelecionado, fileBase64: base64 });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Remessas Pagamento</h1>
        <p className="text-sm text-gray-500">Histórico operacional de pagamentos Itaú CNAB 240.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Remessas</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-y">
              <tr>
                <th className="p-3 text-left">Remessa</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Emitente</th>
                <th className="p-3 text-left">Conta Itaú</th>
                <th className="p-3 text-right">Itens</th>
                <th className="p-3 text-right">Valor</th>
                <th className="p-3 text-left">Incluída em</th>
                <th className="p-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {(remessas.data ?? []).map((remessa: any) => (
                <tr key={remessa.numeroRemessa} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">#{remessa.numeroRemessa}</td>
                  <td className="p-3">{textoStatus(remessa.status)}</td>
                  <td className="p-3">{remessa.emitenteNome || "-"}</td>
                  <td className="p-3">{remessa.bancoNome || "Itaú"} — ag. {remessa.agencia || "-"} / conta {remessa.conta || "-"}</td>
                  <td className="p-3 text-right">{remessa.quantidadeAtiva}{Number(remessa.quantidadeCancelada) > 0 ? ` ativos, ${remessa.quantidadeCancelada} cancelados` : ""}</td>
                  <td className="p-3 text-right font-mono">{formatarMoeda(remessa.valorTotal)}</td>
                  <td className="p-3">{formatarData(remessa.incluidoEm)}</td>
                  <td className="p-3 text-center space-x-2 whitespace-nowrap">
                    <button className="text-blue-600 hover:underline" onClick={() => setNumeroRemessaSelecionado(Number(remessa.numeroRemessa))}>Ver</button>
                  </td>
                </tr>
              ))}
              {remessas.isLoading && <tr><td colSpan={8} className="p-5 text-center text-gray-500">Carregando...</td></tr>}
              {!remessas.isLoading && (remessas.data ?? []).length === 0 && <tr><td colSpan={8} className="p-5 text-center text-gray-500">Nenhuma remessa de pagamento encontrada.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {numeroRemessaSelecionado !== null && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Remessa #{numeroRemessaSelecionado}</CardTitle>
              <p className="text-sm text-gray-500 mt-1">{textoStatus(statusSelecionado || "cancelado")}{retornoProcessado ? " — retorno processado" : ""}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setNumeroRemessaSelecionado(null); setArquivoRetorno(null); }}>Fechar</Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {detalhes.isLoading && <p className="text-sm text-gray-500">Carregando detalhes...</p>}
            {!detalhes.isLoading && (
              <>
                <div className="flex flex-wrap gap-2">
                  {statusSelecionado === "rascunho" && (
                    <Button size="sm" onClick={() => gerarArquivo.mutate({ numeroRemessa: numeroRemessaSelecionado })} disabled={gerarArquivo.isPending}>
                      {gerarArquivo.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                      Gerar arquivo CNAB
                    </Button>
                  )}
                  {remessaSelecionada?.arquivoCnab && <Button variant="outline" size="sm" onClick={() => baixar("cnab")}><Download className="h-4 w-4 mr-1" />Baixar CNAB</Button>}
                  {statusSelecionado === "gerado" && !retornoProcessado && (
                    <Button variant="destructive" size="sm" onClick={() => { if (confirm("Cancelar esta remessa? Os pagamentos gerados voltarão para pendente.")) cancelarRemessa.mutate({ numeroRemessa: numeroRemessaSelecionado }); }} disabled={cancelarRemessa.isPending}>Cancelar remessa</Button>
                  )}
                  {remessaSelecionada?.arquivoRetorno && <Button variant="outline" size="sm" onClick={() => baixar("retorno")}><Download className="h-4 w-4 mr-1" />Baixar retorno</Button>}
                </div>

                {statusSelecionado === "gerado" && !retornoProcessado && (
                  <div className="border rounded p-3 space-y-2">
                    <p className="font-medium text-sm">Retorno Itaú</p>
                    <p className="text-xs text-gray-500">O envio processa o arquivo: ocorrências 00 baixam; não efetivadas ou ausentes retornam a pendente.</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input className="max-w-md" type="file" accept=".ret,.txt" onChange={(evento) => setArquivoRetorno(evento.target.files?.[0] ?? null)} />
                      <Button size="sm" onClick={processarArquivoRetorno} disabled={!arquivoRetorno || enviarRetorno.isPending}>
                        {enviarRetorno.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Enviar e processar retorno
                      </Button>
                    </div>
                  </div>
                )}

                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="p-2 text-left">Conta</th>
                        <th className="p-2 text-left">Descrição</th>
                        <th className="p-2 text-left">Fornecedor</th>
                        <th className="p-2 text-center">Vencimento</th>
                        <th className="p-2 text-right">Valor</th>
                        <th className="p-2 text-center">Tipo</th>
                        <th className="p-2 text-center">Status</th>
                        <th className="p-2 text-center">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((item: any) => (
                        <tr key={item.id} className="border-b">
                          <td className="p-2">#{item.contaPagarId}</td>
                          <td className="p-2">{item.descricao || "-"}</td>
                          <td className="p-2">{item.fornecedorNome || "-"}</td>
                          <td className="p-2 text-center">{item.dataVencimento ? new Date(`${item.dataVencimento}T12:00:00`).toLocaleDateString("pt-BR") : "-"}</td>
                          <td className="p-2 text-right font-mono">{formatarMoeda(item.valor)}</td>
                          <td className="p-2 text-center">{item.tipoPagamento || "-"}</td>
                          <td className="p-2 text-center">{textoStatus(item.status)}</td>
                          <td className="p-2 text-center">
                            {item.status === "rascunho" && statusSelecionado === "rascunho" ? (
                              <button className="inline-flex items-center gap-1 text-red-600 hover:underline" onClick={() => { if (confirm("Remover este pagamento da remessa?")) removerPagamento.mutate({ numeroRemessa: numeroRemessaSelecionado, contaPagarId: Number(item.contaPagarId) }); }}>
                                <Trash2 className="h-3 w-3" />Remover
                              </button>
                            ) : "-"}
                          </td>
                        </tr>
                      ))}
                      {itens.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-gray-500">Nenhum item encontrado.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
